import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { brl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Minus, ShoppingBag, X } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

export default function PublicMenu() {
  const { slug } = useParams();
  const nav = useNavigate();
  const myOrders = JSON.parse(localStorage.getItem(`pg_orders_${slug}`) || "[]");
  const [data, setData] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "", notes: "" });
  const [payment, setPayment] = useState("dinheiro");

  useEffect(() => { publicApi.get(`/public/menu/${slug}`).then((r) => setData(r.data)).catch(() => {}); }, [slug]);
  const byCat = useMemo(() => {
    if (!data) return {};
    const m = {}; data.categories.forEach((c) => (m[c.id] = { cat: c, items: [] }));
    const other = { cat: { id: "__outros", name: "Outros" }, items: [] };
    data.products.forEach((p) => { if (m[p.category_id]) m[p.category_id].items.push(p); else other.items.push(p); });
    if (other.items.length) m.__outros = other;
    return m;
  }, [data]);

  const add = (p) => setCart((c) => {
    const ex = c.find((i) => i.product_id === p.id);
    if (ex) return c.map((i) => i.product_id === p.id ? { ...i, quantity: i.quantity + 1 } : i);
    return [...c, { product_id: p.id, name: p.name, price: p.price, quantity: 1 }];
  });
  const upd = (id, d) => setCart((c) => c.map((i) => i.product_id === id ? { ...i, quantity: Math.max(0, i.quantity + d) } : i).filter((i) => i.quantity > 0));
  const total = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  const submit = async () => {
    if (!customer.name || !customer.phone) return toast.error("Preencha nome e telefone");
    if (cart.length === 0) return toast.error("Adicione itens");
    try {
      const r = await publicApi.post(`/public/menu/${slug}/order`, {
        channel: "delivery", items: cart, customer_name: customer.name, customer_phone: customer.phone,
        address: customer.address, payment_method: payment, notes: customer.notes,
      });
      const key = `pg_orders_${slug}`; const prev = JSON.parse(localStorage.getItem(key) || "[]");
      localStorage.setItem(key, JSON.stringify([r.data.id, ...prev].slice(0, 10)));
      setCart([]); setShowCart(false);
      nav(`/pedido/${slug}/${r.data.id}`);
    } catch (e) { toast.error("Erro ao enviar pedido"); }
  };

  if (!data) return <div className="min-h-screen grid place-items-center text-stone-500">Carregando cardápio...</div>;
  const r = data.restaurant;

  return (
    <div className="min-h-screen bg-stone-50 pb-32">
      <div className="relative h-56 bg-stone-900" style={{ backgroundColor: r.brand_color || "#1C1917" }}>
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <h1 className="font-display text-3xl font-extrabold">{r.name}</h1>
          <p className="text-sm opacity-90">{r.address || "Cardápio digital"}</p>
          {myOrders.length > 0 && <Link to={`/pedido/${slug}/${myOrders[0]}`} className="inline-block mt-2 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold" data-testid="menu-track-link">Acompanhar meu último pedido →</Link>}
        </div>
      </div>
      <div className="max-w-3xl mx-auto p-4 space-y-6">
        {Object.values(byCat).filter((g) => g.items.length > 0).map((g) => (
          <div key={g.cat.id}>
            <h2 className="font-display text-xl font-bold text-stone-900 mb-3" data-testid={`cat-${g.cat.id}`}>{g.cat.name}</h2>
            <div className="space-y-2">
              {g.items.map((p) => (
                <button key={p.id} onClick={() => add(p)} data-testid={`menu-product-${p.id}`}
                  className="w-full flex items-center gap-3 rounded-xl bg-white p-3 border border-stone-200 hover:border-orange-400 transition text-left">
                  {p.image_url && <img src={p.image_url} alt="" className="h-16 w-16 object-cover rounded-lg" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-stone-900">{p.name}</div>
                    <div className="text-xs text-stone-500 line-clamp-2">{p.description}</div>
                    <div className="mt-1 font-mono-num font-bold text-orange-600">{brl(p.price)}</div>
                  </div>
                  <div className="h-8 w-8 rounded-lg bg-orange-600 text-white grid place-items-center shrink-0"><Plus className="h-4 w-4" /></div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {cart.length > 0 && (
        <button onClick={() => setShowCart(true)} data-testid="menu-cart-btn"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-orange-600 text-white px-6 py-3 shadow-xl flex items-center gap-3 font-bold">
          <ShoppingBag className="h-5 w-5" />
          <span className="font-mono-num">{cart.length} • {brl(total)}</span>
        </button>
      )}
      {showCart && (
        <div className="fixed inset-0 bg-black/50 z-40 grid place-items-end" onClick={() => setShowCart(false)}>
          <div className="w-full bg-white rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-extrabold">Seu pedido</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowCart(false)}><X className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-2 mb-4">
              {cart.map((i) => (
                <div key={i.product_id} className="flex items-center gap-2 border-b border-stone-100 pb-2">
                  <div className="flex-1"><div className="text-sm font-medium">{i.name}</div>
                    <div className="text-xs font-mono-num text-stone-500">{brl(i.price)}</div></div>
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => upd(i.product_id, -1)}><Minus className="h-3 w-3" /></Button>
                  <span className="w-5 text-center">{i.quantity}</span>
                  <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => upd(i.product_id, 1)}><Plus className="h-3 w-3" /></Button>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <Input placeholder="Seu nome" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} data-testid="menu-name" />
              <Input placeholder="Telefone" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="menu-phone" />
              <Input placeholder="Endereço (entrega)" value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} data-testid="menu-address" />
              <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm capitalize" value={payment} onChange={(e) => setPayment(e.target.value)} data-testid="menu-payment">
                {["dinheiro","pix","debito","credito"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <Textarea placeholder="Observações" value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} />
              <div className="flex items-baseline justify-between pt-2 border-t"><span>Total</span><span className="font-mono-num text-2xl font-extrabold text-orange-600">{brl(total)}</span></div>
              <Button className="w-full bg-orange-600 hover:bg-orange-700 h-12" onClick={submit} data-testid="menu-submit">Enviar pedido</Button>
            </div>
          </div>
        </div>
      )}
      <Toaster position="top-center" richColors />
    </div>
  );
}
